import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';
import { AssistantComposerComponent } from 'src/app/ai/components/assistant-composer/assistant-composer.component';
import { DocumentationArticleDialogComponent } from 'src/app/@shared/components/documentation/documentation-article-dialog/documentation-article-dialog.component';
import {
    DocAssistantContext,
    DocumentationAssistantComponent,
} from 'src/app/@shared/components/documentation/documentation-assistant/documentation-assistant.component';
import { DocumentationTypeBadgeComponent } from 'src/app/@shared/components/documentation/documentation-type-badge/documentation-type-badge.component';
import { DocLinkTarget } from 'src/app/@shared/components/documentation/documentation-content/documentation-content.component';

// Modèles
import { DocArticle } from 'src/app/@shared/models/documentation/documentation.types';

/**
 * La documentation de l'écran affiché : les articles du manuel qui le traitent,
 * chacun avec la question à laquelle il répond et sa nature.
 *
 * L'écran courant est déclaré par la route (`data: { screen }`), et son nom par
 * le `title` de cette même route ; le rattachement écran → articles vient du
 * manuel (`index.json`). Le bouton n'apparaît pas quand l'écran n'a aucun
 * article — comme le bouton d'historique quand il n'a rien à montrer.
 *
 * Son icône est un **livre ouvert**, pas un « ? » : le « ? » de la barre d'outils
 * d'une liste explique l'écran, celui-ci mène au manuel. Deux gestes différents,
 * deux icônes (arbitrage #966).
 *
 * C'est aussi la porte d'entrée de l'assistant du manuel là où la question naît
 * (#970) : le champ « Poser une question sur cet écran » bascule le panneau en
 * conversation. Ce qui s'ouvre depuis ici reste **en fenêtre** — on ne perd
 * jamais l'écran sur lequel on travaille.
 */
@Component({
    selector: 'app-documentation-help-indicator',
    imports: [
        ButtonModule,
        PopoverModule,
        TooltipModule,
        AssistantComposerComponent,
        DocumentationAssistantComponent,
        DocumentationTypeBadgeComponent,
    ],
    templateUrl: './documentation-help-indicator.component.html'
})
export class DocumentationHelpIndicatorComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild('helpPopover') public helpPopover?: Popover;

    @ViewChild('assistant') private _assistant?: DocumentationAssistantComponent;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _articles: DocArticle[] = [];
    public get articles(): DocArticle[] {
        return this._articles;
    }

    /** Mémoïsation du contexte passé à l'assistant — cf. l'accesseur. */
    private _assistantContext: DocAssistantContext | null = null;

    /**
     * Le panneau montre la conversation à la place de la liste. L'état survit à
     * une navigation : une conversation ouverte reste lisible tant qu'on ne l'a
     * pas quittée — c'est la question **suivante** qui en ouvrira une neuve, avec
     * le nouvel écran.
     */
    private _inConversation = false;
    public get inConversation(): boolean {
        return this._inConversation;
    }

    private _screen: string | null = null;
    public get screen(): string | null {
        return this._screen;
    }

    /**
     * Le nom de l'écran affiché, repris du `title` de la route — celui de
     * l'onglet du navigateur. C'est un **nom d'écran** (« Facture de vente »), pas
     * le libellé de la pièce ouverte (« Facture F-2026-0042 ») qu'affiche le fil
     * d'Ariane : l'assistant ne connaît rien des données du dossier, un numéro de
     * pièce ne lui dit pas s'il s'agit d'un achat ou d'une vente. Et le titre de
     * route est posé **avec** la route, quand le fil d'Ariane d'une page de détail
     * ne l'est qu'une fois la pièce chargée — d'où des questions parties avec un
     * nom vide (#1122).
     */
    private _screenLabel = '';
    public get screenLabel(): string {
        return this._screenLabel;
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /**
     * Ce que la question dira d'elle-même : l'écran affiché. C'est aussi ce qui
     * identifie la conversation — en changer en ouvre une neuve.
     *
     * L'objet est mémoïsé : il est lié au gabarit, et en fabriquer un neuf à
     * chaque cycle de détection rejouerait l'`@Input` sans fin. On ne le refait
     * que quand l'écran ou son nom bouge.
     */
    public get assistantContext(): DocAssistantContext | null {
        const label = this.screenLabel;
        if (!this._screen) {
            this._assistantContext = null;
        } else if (this._assistantContext?.id !== this._screen || this._assistantContext.label !== label) {
            this._assistantContext = { kind: 'screen', id: this._screen, label, scope: `screen:${this._screen}` };
        }
        return this._assistantContext;
    }

    /**
     * L'assistant de support n'existe pas encore : le lien du pied reste masqué
     * jusqu'à la livraison de #944, où il deviendra son point d'entrée.
     */
    public get supportAvailable(): boolean {
        return false;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickArticle(article: DocArticle): void {
        this._showArticle(article.id);
    }

    public onClickBackToArticles(): void {
        this._inConversation = false;
    }

    public onClickHelp(event: Event): void {
        this.helpPopover?.toggle(event);
    }

    public onClickHome(): void {
        this._goToHome();
    }

    public onClickSupport(): void {
        this._goToSupport();
    }

    /** Citation de l'assistant : elle s'ouvre en fenêtre, comme un article de la liste. */
    public onOpenDoc(target: DocLinkTarget): void {
        this._showArticle(target.id, target.anchor);
    }

    public onSendQuestion(question: string): void {
        this._askAssistant(question);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override loadData(): void {
        this._resolveScreen();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * La conversation n'est montée qu'en mode conversation : on attend le rendu
     * pour lui passer la question.
     */
    private _askAssistant(question: string): void {
        if (!question.trim()) {
            return;
        }
        this._inConversation = true;
        setTimeout(() => this._assistant?.ask(question));
    }

    /** La route la plus profonde — celle qui porte la page affichée. */
    private _deepestRoute(): ActivatedRoute {
        let route: ActivatedRoute = this.router.routerState.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    /**
     * Nouvel onglet, et non navigation : le panneau s'ouvre depuis n'importe quel
     * écran, souvent une saisie en cours. Aller lire le manuel ne doit pas coûter
     * ce qu'on était en train de faire.
     */
    private _goToHome(): void {
        this.helpPopover?.hide();
        const url = this.router.serializeUrl(this.router.createUrlTree([this.appService.clientUrl('/documentation')]));
        window.open(url, '_blank');
    }

    /** Point d'entrée de l'assistant de support — à câbler à la livraison de #944. */
    private _goToSupport(): void {
        this.helpPopover?.hide();
    }

    /** Écran déclaré par la route la plus profonde. */
    private _readScreenFromRoute(): string | null {
        return this._deepestRoute().snapshot.data['screen'] ?? null;
    }

    /** Nom de l'écran : le `title` de la route la plus profonde — cf. `screenLabel`. */
    private _readScreenLabelFromRoute(): string {
        return this._deepestRoute().snapshot.title ?? '';
    }

    /**
     * Rattachement de l'écran courant. L'index du manuel est chargé une fois
     * puis servi de mémoire : les navigations suivantes ne coûtent rien.
     */
    private _resolveScreen(): void {
        this._screen = this._readScreenFromRoute();
        this._screenLabel = this._readScreenLabelFromRoute();
        this._articles = [];
        if (!this._screen || !this.appService.selectedClient) {
            return;
        }
        const screen = this._screen;
        this.subscriptions.add(
            this.documentationService.loadIndex$().subscribe({
                next: () => this._articles = this.documentationService.articlesOfScreen(screen),
                error: () => this._articles = [],
            })
        );
    }

    /**
     * L'aide d'un écran s'ouvre en fenêtre : on la referme et on reprend son
     * travail où on l'avait laissé. La page complète reste accessible depuis la
     * fenêtre, pour qui veut explorer plutôt que dépanner. Une citation de
     * l'assistant suit exactement le même chemin — la réponse ne doit jamais
     * coûter l'écran sur lequel on travaille.
     */
    private _showArticle(id: string, anchor?: string): void {
        this.helpPopover?.hide();
        this.openDialog(DocumentationArticleDialogComponent, 'DOCUMENTATION_DIALOG', { docId: id, docAnchor: anchor });
    }
}
